<script setup lang="ts">
const { t, locale } = useI18n({
  useScope: 'local',
});

const { systemAPI } = useBackendRequests();
const accountStore = useAccountStore();
const { formatNumber } = useNumberFormatter();
const { getTrackingConfig } = useTrackingConfig();
const trackingButton = getTrackingConfig('search', 'buttons','shoutOutActivateSubscription');
const trackingElement = getTrackingConfig('search', 'elements','shoutOutActivateSubscription');

const lowestPrice = ref<string | null>(null);
const staticLinks = useStaticLinks();
const randomNumHeadline = Math.floor(Math.random() * 3);
const randomNumText = Math.floor(Math.random() * 2);

const productCity = accountStore?.housing?.geo?.city ?? 'Berlin';
const productCountry = accountStore?.housing?.geo?.iso3Country ?? 'DEU';

const headline = computed(() =>
  randomNumHeadline === 1
    ? t(`headline.enumerations[${randomNumHeadline}]`, { lowestPricePerMonth: lowestPrice.value })
    : t(`headline.enumerations[${randomNumHeadline}]`)
);

const subHeadline = computed(() =>
  randomNumText === 0
    ? t(`text.enumerations[${randomNumText}]`, { lowestPricePerMonth: lowestPrice.value })
    : t(`text.enumerations[${randomNumText}]`)
);


async function getLowestPrice() {
  try {
    const products = await systemAPI.getActiveProductsByProductKeyCity('b2c-subscription', productCity, productCountry);
    if (products.data.length) {
      const minPrice = products.data.reduce((minPrice, product) => {
        return product.priceGrossPer30Days < minPrice ? product.priceGrossPer30Days : minPrice;
      }, Infinity);

      lowestPrice.value = formatNumber(minPrice, locale.value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  } catch (error) {
    console.error(error);
  }
}

onMounted(() => {
 getLowestPrice();
});

</script>

<template>
  <div
    class="shout-out-box tw-tracking"
    :aria-labelledby="headline"
    :aria-describedby="subHeadline"
    :data-tracking-id="trackingElement.dataTrackingId[randomNumHeadline]"
    :data-page="trackingElement.dataPage"
    :data-headline="headline"
    :data-subheadline="subHeadline"
  >
    <h2>{{ headline }}</h2>
    <p>{{ subHeadline }}</p>
    <div>
      <NuxtLink
        :to="staticLinks.billing"
        class="button tw-tracking"
        :data-page="trackingButton.dataPage"
        :data-tracking-id="trackingButton.dataTrackingId"
        :data-headline="headline"
        :data-subheadline="subHeadline"
        :data-button-text="t('activate_now')"
        data-button-icon="cart-dash"
        data-button-color="primary"
      >
        <span class="icon"><Icon name="bi:cart-dash" /></span>
        {{ t('activate_now') }}
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/tw-variables.scss';
@import '@/assets/styles/tw-mixins.scss';

.shout-out-box {
  @include shout-out;
}
.shout-out-box {
  padding: 1rem;
}

.icon {
  margin-right: 0.5rem;
}
</style>

<i18n lang="json">
{
  "de": {
    "headline": {
      "enumerations": [
        "Mit Pro 4,6x erfolgreicher tauschen 🚀",
        "Hol dir die Pro Mitgliedschaft ab {lowestPricePerMonth} € pro Monat",
        "Schalte alle Funktionen frei 🚀"
      ]
    },
    "text": {
      "enumerations": [
        "Analysen zeigen, dass Pro Mitglieder 4,6x wahrscheinlicher tauschen. Hol dir die Pro Mitgliedschaft ab {lowestPricePerMonth} € pro Monat.",
        "Wusstest du, dass Pro Mitglieder 4,6x wahrscheinlicher tauschen? Schalte alle Funktionen frei und finde schneller deine neue Wohnung."
      ]
    },
    "activate_now": "Jetzt freischalten"
  },
  "en": {
    "headline": {
      "enumerations": [
        "Exchange 4.6x more successfully with Pro 🚀",
        "Get the Pro membership from {lowestPricePerMonth} € per month",
        "Unlock all features 🚀"
      ]
    },
    "text": {
      "enumerations": [
        "Analyses show that Pro members are 4.6x more likely to exchange. Get the Pro membership from {lowestPricePerMonth} € per month.",
        "Did you know that Pro members are 4.6x more likely to exchange? Unlock all features and find your new home faster."
      ]
    },
    "activate_now": "Activate now"
  }
}
</i18n>
